import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { object, string } from 'yup';
import { useFormik } from 'formik';

// Store
import { Dispatch, RootState } from '../../store/store';

// Constants
import { TEXT_LENGTH_LIMITS } from '../../constants/common';

// Components
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import InputControl from '../../components/InputControl/InputControl';
import Switcher, { ResponseSwitch } from '../../components/Switcher/Switcher';
import CycleMeasurementSymbol from '../../enums/cycle-measurement-symbol';
import { useTranslation } from 'react-i18next';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import LocationMetaFields from '../../components/LocationMetaFields/LocationMetaFields';

type FormValues = {
  name: string | null;
  city: string | null;
  isCelsius: boolean;
};

type EditLocationProps = ReturnType<typeof mapDispatch> & ReturnType<typeof mapState>;

const EditLocation: React.FC<EditLocationProps> = ({ getLocation, updateLocation, location }) => {
  const { id } = useParams();

  const [isLocationLoaded, setIsLocationLoaded] = useState(false);
  const { t } = useTranslation();

  const formik = useFormik<FormValues>({
    initialValues: {
      name: null,
      isCelsius: location?.isCelsius ?? false,
      city: null,
    },
    validationSchema: object({
      name: string()
        .required(t('clients.form.validation.name.required'))
        .min(TEXT_LENGTH_LIMITS.MIN, t('clients.form.validation.name.minLength', '', { min: TEXT_LENGTH_LIMITS.MIN }))
        .max(TEXT_LENGTH_LIMITS.MAX, t('clients.form.validation.name.maxLength', '', { max: TEXT_LENGTH_LIMITS.MAX })),
      city: string()
        .required(t('clients.form.validation.city.required'))
        .min(TEXT_LENGTH_LIMITS.MIN, t('clients.form.validation.city.minLength', '', { min: TEXT_LENGTH_LIMITS.MIN }))
        .max(TEXT_LENGTH_LIMITS.MAX, t('clients.form.validation.city.maxLength', '', { max: TEXT_LENGTH_LIMITS.MAX })),
    }),
    onSubmit: async (values) => {
      const { name, city, isCelsius } = values;
      await updateLocation({ id, formData: { name, city, isCelsius } });
    },
  });

  const isDisabledBtn = Boolean(
    (formik.touched.name && formik.errors.name) || (formik.touched.city && formik.errors.city),
  );

  const fetchLocations = async (id: string) => {
    const response = await getLocation(id);
    const locationData = response?.location;

    if (locationData) {
      Object.entries(locationData).forEach(([key, value]) => {
        formik.handleChange({
          target: {
            name: key,
            value: value,
          },
        });
      });
    }

    setIsLocationLoaded(true);
  };

  const onUnitsChange = (data: ResponseSwitch) => {
    formik.setFieldValue('isCelsius', data.value);
  };

  useEffect(() => {
    fetchLocations(id);
  }, [id]);

  if (!isLocationLoaded) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <SpinnerLoader />
      </div>
    );
  }

  return (
    <div className="p-edit-location pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="font-medium text-black dark:text-white">{t('clients.form.heads.edit')}</h3>
        </div>
        <form className="flex flex-col gap-5.5 p-7" onSubmit={formik.handleSubmit}>
          <div className="w-full">
            <InputControl
              label={t('clients.form.inputs.name.label')}
              type="text"
              name="name"
              placeholder={t('clients.form.inputs.name.placeholder')}
              value={formik.values.name}
              customOnChange={formik.handleChange}
              onBlur={formik.handleBlur}
              showError={formik.touched.name && formik.errors.name}
              errorText={formik.errors.name}
            />
          </div>
          <div className="w-full">
            <InputControl
              label={t('clients.form.inputs.city.label')}
              type="text"
              name="city"
              placeholder={t('clients.form.inputs.city.placeholder')}
              value={formik.values.city}
              customOnChange={formik.handleChange}
              onBlur={formik.handleBlur}
              showError={formik.touched.city && formik.errors.city}
              errorText={formik.errors.city}
            />
          </div>
          <div className="c-input-control__input-wrapper">
            <p className="flex justify-between py-2.5 font-medium text-black dark:text-white">
              {t('clients.form.inputs.units.label')}
            </p>
            <Switcher
              options={{
                left: CycleMeasurementSymbol.Celsius,
                right: CycleMeasurementSymbol.Fahrenheit,
              }}
              isEnable={formik.values.isCelsius}
              onChange={onUnitsChange}
              switcherId={`${formik.values.name}-location-units-id`}
            />
            <div className="flex min-h-6 items-center justify-start"></div>
          </div>
          <div className="flex justify-end gap-4.5">
            <InteractiveButton
              text={t('buttons.update')}
              submit
              disabled={isDisabledBtn}
              isLoading={formik.isSubmitting}
            />
          </div>
        </form>
      </div>
      <LocationMetaFields />
    </div>
  );
};

const mapState = (state: RootState) => ({
  location: state.locations.location,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getLocation: dispatch.locations.getLocation,
  updateLocation: dispatch.locations.updateLocation,
});

export default connect(mapState, mapDispatch)(EditLocation);
